import axios from 'axios'
import React, { useState, useEffect } from 'react'
import { Button } from 'reactstrap'
import { useMsal, useAccount } from '@azure/msal-react'
import { joinUrl } from '../utils/urlUtils'
import { getRuntimeConfig } from '../utils/configUtils'
import { acquireApiToken } from '../utils/authTokenUtils'

interface ServeReportComponentProps {
  reportName: string
  reportType: 'playwright' | 'monocart'
  label?: string
  className?: string
}

const ServeReportComponent: React.FC<ServeReportComponentProps> = ({ reportName, reportType, label, className }) => {
  const { instance } = useMsal()
  const account = useAccount()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const errorTimeoutId = React.useRef<NodeJS.Timeout | null>(null)

  let viewReportText = label ?? 'View Report'
  if (!label) {
    if (reportType === 'playwright') {
      viewReportText = 'Playwright'
    } else if (reportType === 'monocart') {
      viewReportText = 'Monocart'
    }
  }

  useEffect(() => {
    return () => {
      if (errorTimeoutId.current) {
        clearTimeout(errorTimeoutId.current)
      }
    }
  }, [])

  const resetErrorAfterDelay = () => {
    if (errorTimeoutId.current) {
      clearTimeout(errorTimeoutId.current)
    }

    errorTimeoutId.current = setTimeout(() => {
      setError(null)
    }, 10000)
  }

  const serveHtmlReport = async (reportName: string) => {
    try {
      setLoading(true)
      setError(null)

      if (!account) {
        console.log('No active account found')
        throw new Error('No active account')
      }

      console.log('Getting token for serving report...')
      const accessToken = await acquireApiToken(instance, account)

      const apiUrl = getRuntimeConfig('REACT_APP_API_URL')

      if (!apiUrl) {
        throw new Error('API URL is not configured')
      }

      const requestUrl = joinUrl(apiUrl, `/api/reports/${reportName}/zip/${reportType}`)
      console.log('Requesting report URL from:', requestUrl)

      const apiResponse = await axios.get(requestUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      })

      console.log('Response received:', {
        status: apiResponse.status,
        statusText: apiResponse.statusText,
        data: apiResponse.data,
      })

      const reportUrl = apiResponse.data.url
      const fullReportUrl = reportUrl.startsWith('http') ? reportUrl : joinUrl(apiUrl, reportUrl)

      console.log('Opening full report URL:', fullReportUrl)

      window.open(fullReportUrl, '_blank', 'noopener,noreferrer')
    } catch (error: any) {
      console.error('Error serving report:', error)
      console.log('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data,
      })
      setError('Failed to serve report')
      resetErrorAfterDelay()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {error && <div className="error-message">{error}</div>}
      <Button color="success" className={className} onClick={() => serveHtmlReport(reportName)} disabled={loading}>
        {loading ? 'Loading...' : viewReportText}
      </Button>
    </div>
  )
}

export default ServeReportComponent
